import React, { useMemo } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import { buildAxis, computeDateBounds, groupRowsIntoSections, offsetPercent, ragTone } from './viewModeUtils';

import StarBadgeIcon from 'Assets/icons/star-badge.svg';

const BLANK_PLACEHOLDER = '—';

// Minimum visible bar width (%) so a single-day / zero-span activity still
// renders as a clickable sliver rather than a hairline.
const MIN_BAR_WIDTH = 1.5;

// A row is "undated" only when it has neither a start nor a target date.
const isUndated = (row) => !row.start_date && !row.target_completion_date;

const barToneClass = (tone) =>
    classNames({
        '!tw-bg-red-500': tone === 'red',
        '!tw-bg-yellow-400': tone === 'amber',
        '!tw-bg-green-500': tone === 'green',
        'tw-bg-charcoal-25': tone === 'neutral',
    });

/**
 * Resolve the geometry of an activity's Gantt bar (and any slip overlay) as
 * left/width percentages along the axis. A row slips when it is flagged as an
 * exception and its estimated date runs past its target.
 */
const resolveBar = (row, bounds) => {
    const start = row.start_date || row.target_completion_date;
    const end = row.target_completion_date || row.start_date;

    const left = offsetPercent(start, bounds);
    const right = offsetPercent(end, bounds);
    const width = Math.max(right - left, MIN_BAR_WIDTH);

    let slip = null;

    if (row.is_exception && row.estimated_completion_date && moment(row.estimated_completion_date).isAfter(moment(end))) {
        const slipRight = offsetPercent(row.estimated_completion_date, bounds);
        slip = { left: right, width: Math.max(slipRight - right, MIN_BAR_WIDTH) };
    }

    return { left, width, slip };
};

const GanttRow = ({ row, bounds, columns }) => {
    const tone = ragTone(row);
    const bar = resolveBar(row, bounds);
    const target = row.target_completion_date ? moment(row.target_completion_date).format('DD-MM-YYYY') : BLANK_PLACEHOLDER;
    const estimated = row.estimated_completion_date ? moment(row.estimated_completion_date).format('DD-MM-YYYY') : BLANK_PLACEHOLDER;

    return (
        <div className="tw-flex tw-border-b last:tw-border-b-0 tw-border-grey">
            <div className="tw-w-56 tw-shrink-0 tw-px-3 tw-py-2 tw-text-xs tw-text-charcoal tw-border-e tw-border-grey tw-flex tw-items-center tw-gap-x-1.5">
                {row.is_key_milestone && <StarBadgeIcon className="tw-h-3 tw-w-3 tw-text-blue tw-shrink-0" title={$t('Key milestone')} />}
                <span className="tw-truncate">{row.description}</span>
            </div>
            <div className="tw-relative tw-flex-auto tw-h-8">
                {/* Column gridlines */}
                <div className="tw-absolute tw-inset-0 tw-flex tw-pointer-events-none">
                    {columns.map((column) => (
                        <div key={column.key} className="tw-flex-1 tw-border-e last:tw-border-e-0 tw-border-grey/60" />
                    ))}
                </div>
                {/* Slip overlay (target → estimated) sits under the bar */}
                {bar.slip && (
                    <div
                        className="tw-absolute tw-top-1/2 tw--translate-y-1/2 tw-h-2 tw-rounded-full tw-bg-red/25"
                        style={{ insetInlineStart: `${bar.slip.left}%`, inlineSize: `${bar.slip.width}%`, transform: 'translateY(-50%)' }}
                        title={`${$t('Estimated')}: ${estimated}`}
                    />
                )}
                <div
                    className={classNames('tw-absolute tw-top-1/2 tw--translate-y-1/2 tw-h-3.5 tw-rounded-full', barToneClass(tone))}
                    style={{ insetInlineStart: `${bar.left}%`, inlineSize: `${bar.width}%`, transform: 'translateY(-50%)' }}
                    title={`${row.description} · ${$t('Target')}: ${target}`}
                />
            </div>
        </div>
    );
};

/**
 * Gantt view (IB-674).
 *
 * One bar per activity spanning its start → target date, with a translucent
 * slip overlay out to the estimated date when the activity is an exception. RAG
 * status (or overdue) colours the bar. Undated rows are listed separately. On a
 * multi-year Days axis the columns are coarsened to Months to keep the axis
 * bounded, with a notice.
 */
const GanttView = ({ rows, timeView }) => {
    const bounds = useMemo(() => computeDateBounds(rows), [rows]);
    const sections = useMemo(() => groupRowsIntoSections(rows), [rows]);

    // Multi-year Days guard (shared with TimelineView + the parent notice).
    const axis = useMemo(() => buildAxis(bounds, timeView), [bounds, timeView]);

    const datedSections = useMemo(
        () => sections.map((section) => ({ ...section, rows: section.rows.filter((row) => !isUndated(row)) })).filter((section) => section.rows.length > 0),
        [sections],
    );

    const undatedRows = useMemo(() => (rows || []).filter((row) => !row._is_group && isUndated(row)), [rows]);

    const hasAxis = !!bounds && axis.columns.length > 0;

    // A fully date-less filtered set (bounds null) must still surface its
    // activities in the Undated list — never drop them behind the empty state.
    if (!hasAxis && undatedRows.length === 0) {
        return (
            <div className="tw-p-6 tw-text-sm tw-text-charcoal-75 tw-bg-grey/40 tw-rounded-lg">
                {$t('No dated activities to plot on the Gantt chart for the current criteria.')}
            </div>
        );
    }

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-4">
            {hasAxis && (
                <div className="tw-border tw-border-grey tw-rounded-lg tw-overflow-hidden">
                    {/* Period axis */}
                    <div className="tw-flex tw-bg-background-2 tw-border-b tw-border-grey">
                        <div className="tw-w-56 tw-shrink-0 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-charcoal tw-border-e tw-border-grey">
                            {$t('Activity')}
                        </div>
                        <div className="tw-flex tw-flex-auto">
                            {axis.columns.map((column) => (
                                <div
                                    key={column.key}
                                    className="tw-flex-1 tw-px-1 tw-py-2 tw-text-center tw-text-2xs tw-font-medium tw-text-charcoal-75 tw-border-e last:tw-border-e-0 tw-border-grey tw-truncate"
                                >
                                    {column.label}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Swim-lanes: a category band followed by its activity bars */}
                    {datedSections.map((section, sectionIndex) => (
                        <React.Fragment key={`lane-${sectionIndex}`}>
                            <div className="tw-flex tw-bg-charcoal-15 tw-border-b tw-border-grey">
                                <div className="tw-w-full tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-charcoal">
                                    {section.label || $t('Uncategorised')}
                                </div>
                            </div>
                            {section.rows.map((row, rowIndex) => (
                                <GanttRow key={row.id ?? `${sectionIndex}-${rowIndex}`} row={row} bounds={bounds} columns={axis.columns} />
                            ))}
                        </React.Fragment>
                    ))}
                </div>
            )}

            {undatedRows.length > 0 && (
                <div className="tw-border tw-border-grey tw-rounded-lg tw-p-4">
                    <div className="tw-text-xs tw-font-semibold tw-text-charcoal-75 tw-mb-2">
                        {$t('Undated')} ({undatedRows.length})
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                        {undatedRows.map((row, index) => (
                            <span
                                key={row.id ?? index}
                                className="tw-inline-flex tw-items-center tw-gap-x-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-2xs tw-font-medium tw-bg-grey/50 tw-text-charcoal"
                                title={row.description}
                            >
                                {row.is_key_milestone && <StarBadgeIcon className="tw-h-2.5 tw-w-2.5 tw-text-blue tw-shrink-0" />}
                                <span className="tw-truncate tw-max-w-48">{row.description || BLANK_PLACEHOLDER}</span>
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GanttView;
